import React, { createContext, useContext, useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, query, where, orderBy, getDocs, addDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore';

const CareerDevelopmentContext = createContext();

export function useCareerDevelopment() {
  return useContext(CareerDevelopmentContext);
}

export function CareerDevelopmentProvider({ children }) {
  const [jobListings, setJobListings] = useState([]);
  const [internships, setInternships] = useState([]);
  const [careerResources, setCareerResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch career resources
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [jobsSnapshot, internshipsSnapshot, resourcesSnapshot] = await Promise.all([
          getDocs(collection(db, 'jobListings')),
          getDocs(collection(db, 'internships')),
          getDocs(collection(db, 'careerResources'))
        ]);

        setJobListings(jobsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        setInternships(internshipsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        setCareerResources(resourcesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Create job listing
  const createJobListing = async (data) => {
    try {
      setLoading(true);
      const docRef = await addDoc(collection(db, 'jobListings'), {
        ...data,
        createdAt: new Date(),
        active: true,
        applications: []
      });
      return docRef.id;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Create internship
  const createInternship = async (data) => {
    try {
      setLoading(true);
      const docRef = await addDoc(collection(db, 'internships'), {
        ...data,
        createdAt: new Date(),
        active: true,
        applications: []
      });
      return docRef.id;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Apply for job/internship
  const submitApplication = async (type, id, applicationData) => {
    try {
      setLoading(true);
      const collectionName = type === 'job' ? 'jobListings' : 'internships';
      const docRef = doc(db, collectionName, id);
      const currentData = type === 'job' 
        ? jobListings.find(j => j.id === id)
        : internships.find(i => i.id === id);

      await updateDoc(docRef, {
        applications: [...currentData.applications, {
          ...applicationData,
          submittedAt: new Date(),
          status: 'pending'
        }]
      });
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Add career resource
  const addCareerResource = async (data) => {
    try {
      setLoading(true);
      const docRef = await addDoc(collection(db, 'careerResources'), {
        ...data,
        createdAt: new Date(),
        views: 0
      });
      return docRef.id;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Update application status
  const updateApplicationStatus = async (type, listingId, applicationId, status) => {
    try {
      setLoading(true);
      const collectionName = type === 'job' ? 'jobListings' : 'internships';
      const docRef = doc(db, collectionName, listingId);
      const currentData = type === 'job' 
        ? jobListings.find(j => j.id === listingId)
        : internships.find(i => i.id === listingId);

      const updatedApplications = currentData.applications.map(app => 
        app.id === applicationId ? { ...app, status } : app
      );

      await updateDoc(docRef, { applications: updatedApplications });
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const value = {
    jobListings,
    internships,
    careerResources,
    loading,
    error,
    createJobListing,
    createInternship,
    submitApplication,
    addCareerResource,
    updateApplicationStatus
  };

  return (
    <CareerDevelopmentContext.Provider value={value}>
      {children}
    </CareerDevelopmentContext.Provider>
  );
} 